import React from 'react';
import { useLocation } from 'react-router-dom';

const steps = [
  {
    title: 'Institution Details',
    description: 'Institution information refers to data that identifies or relates to an institution, such as their name, address, contact details, or other identifying attributes.',
    path: '/institute-details'
  },
  {
    title: 'Vacancy Details',
    description: 'Vacancy details outlines the institution\'s vacancies and other information relevant to it.',
    path: '/vacancy-details'
  }
];

const InstitutionStepper = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const getStepStatus = (stepPath) => {
    const stepIndex = steps.findIndex(step => step.path === stepPath);
    const currentIndex = steps.findIndex(step => step.path === currentPath);

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const getStepNumber = (index) => {
    return (index + 1).toString().padStart(2, '0');
  };

  // Mobile Stepper
  const MobileStepper = () => (
    <div className="flex overflow-x-auto pb-4 hide-scrollbar mt-6">
      <div className="flex space-x-4 min-w-max px-2">
        {steps.map((step, index) => {
          const status = getStepStatus(step.path);
          return (
            <div key={index} className="flex items-center">
              {/* Connector Line */}
              {index > 0 && (
                <div 
                  className={`w-8 h-[2px] mx-2 ${
                    status === 'completed' || status === 'current' ? 'bg-primary-500' : 'bg-primary-100'
                  }`}
                />
              )}
              
              {/* Step Circle */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  status === 'completed' || status === 'current' ? 'bg-primary-500 text-primary-50' : 'bg-primary-100'
                }`}>
                  {status === 'completed' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-sm font-medium">{getStepNumber(index)}</span>
                  )}
                </div>
                <span className={`mt-2 text-sm font-medium whitespace-nowrap ${
                  status === 'current' ? 'text-primary-500' : 'text-primary-text'
                }`}>
                  {step.title}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Desktop Stepper
  const DesktopStepper = () => (
    <div className="flex flex-col space-y-8 p-6 bg-primary-extra rounded-lg shadow-sm">
      {steps.map((step, index) => (
        <div key={index} className="relative flex items-start">
          {/* Vertical line */}
          {index < steps.length - 1 && (
            <div 
              className={`absolute left-[15px] top-[30px] w-[2px] h-[calc(100%+10px)] ${
                getStepStatus(step.path) === 'completed' ? 'bg-primary-500' : 'bg-primary-100'
              }`} 
            />
          )}
          
          {/* Step indicator */}
          <div className="relative z-10">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              getStepStatus(step.path) === 'completed' ? 'bg-primary-500 text-primary-50' :
              getStepStatus(step.path) === 'current' ? 'bg-primary-700 text-primary-50' :
              'bg-primary-100 text-primary-text'
            }`}>
              {getStepStatus(step.path) === 'completed' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <span className="text-sm font-medium">{getStepNumber(index)}</span>
              )}
            </div>
          </div>
          
          {/* Step content */}
          <div className="ml-4 flex-1">
            <h3 className={`text-lg font-medium ${
              getStepStatus(step.path) === 'current' ? 'text-primary-700' :
              getStepStatus(step.path) === 'completed' ? 'text-primary-500' :
              'text-gray-500'
            }`}>
              {step.title}
            </h3>
            <p className={`mt-1 text-sm ${
              getStepStatus(step.path) === 'current' || getStepStatus(step.path) === 'completed'
                ? 'text-gray-600'
                : 'text-gray-400'
            }`}>
              {step.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="md:hidden">
        <MobileStepper />
      </div>
      <div className="hidden md:block">
        <DesktopStepper />
      </div>
    </>
  );
};

export default InstitutionStepper;